// Écrit à la main (pas de codegen) : dans ce dossier, seuls schema.ts, queries/
// et requests/ sont générés. Voir .claude/gotchas.md.
import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import type { paths } from "./schema";
import { SESSION_TOKEN_KEY } from "../client/use-session-token";
import {
  reportApiNetworkError,
  reportApiStatus,
  reportRequestStart,
  requestUrl,
} from "../client/api-availability";

const fetchClient = createFetchClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
  credentials: "include",
  fetch: async (...args) => {
    const url = requestUrl(args[0]);
    const settled = reportRequestStart(url);

    let response: Response;
    try {
      try {
        // @ts-expect-error type mismatch
        response = await (window.interceptedFetch ? window.interceptedFetch(...args) : fetch(...args));
      } catch {
        // interceptedFetch also wraps the auth token-refresh interceptor, which rejects
        // with "Failed to fetch" whenever a refresh fails — a stale expired token used to
        // be enough to kill every request here. That says nothing about the API being up,
        // so retry bare before concluding anything. handleRequest rejects before sending,
        // so this cannot duplicate a side effect.
        try {
          response = await fetch(...args);
        } catch (error) {
          reportApiNetworkError(url);
          throw error;
        }
      }
    } finally {
      settled();
    }

    reportApiStatus(url, response.status);
    const data = await response.clone().json().catch(() => null);
    const token = data?.token ?? data?.data?.token;
    if (token) {
      localStorage.setItem(SESSION_TOKEN_KEY, token.slice(0, -12));
    }
    return response;
  },
});

// /public/* s'authentifie par Bearer/corps, jamais par cookie. Y envoyer les
// credentials forcerait le navigateur à exiger Access-Control-Allow-Credentials,
// que le CORS public (origine reflétée) ne renvoie plus — BAB-3040. On filtre sur
// le gabarit de route (schemaPath), indépendant d'un préfixe de baseUrl ; les
// routes authentifiées gardent le cookie (refresh token).
fetchClient.use({
  onRequest({ request, schemaPath }) {
    if (schemaPath.startsWith("/public/")) {
      return new Request(request, { credentials: "omit" });
    }
  },
});

export const $api = createClient(fetchClient);

export const workerFetchClient = fetchClient;

export const productionFetchClient = createFetchClient<paths>({
  baseUrl: "https://api2.baback.co/",
  credentials: "include",
  fetch: async (...args) => {
    // @ts-expect-error type mismatch
    const response = (await window.interceptedFetch?.(...args)) || fetch(...args);
    const data = await response.clone().json().catch(() => null);
    const token = data?.token ?? data?.data?.token;
    if (token) {
      localStorage.setItem(SESSION_TOKEN_KEY, token.slice(0, -12));
    }
    return response;
  },
});

export { fetchClient };
